import { createRoot } from 'react-dom/client';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import * as Sentry from '@sentry/react';

// common
import ConnectedIntlProvider from 'intl/component/ConnectedIntlProvider';
import { PROJECT_LIST_ROUTES } from 'layout/routes';
import { persistor, store } from '@redux/store';
import { applyBrand } from 'config/brand';
import { markChunkReloadRecovered, registerChunkReload } from 'common/utils/chunkReload';
import { installGoogleTranslateDOMPatch } from 'common/utils/fixGoogleTranslateDOM';
import App from './App';
import './index.css';

if (import.meta.env.PROD) {
  Sentry.init({
    dsn: 'https://f251affa8087a185572109b21480ca9e@o4507187749847040.ingest.de.sentry.io/4507187751419984',
    integrations: [
      new Sentry.BrowserTracing({
        // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
      }),
      new Sentry.Replay(),
    ],
    // environment: process.env.REACT_APP_ENVIRONMENT,
    // Performance Monitoring
    tracesSampleRate: 1.0, // Capture 100% of the transactions, reduce in production!
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

const router = createBrowserRouter([
  {
    path: '/*',
    element: <App />,
    children: PROJECT_LIST_ROUTES as any,
  },
]);

// Áp brand (title/favicon) từ /config.js — đồng bộ (config.js đã nạp trước bundle), không fetch BE.
applyBrand();

// Chunk động tải lỗi (deploy dở / index.html cũ trong cache) → tự tải lại trang 1 lần thay vì để
// người dùng nhìn màn "Unexpected Application Error". Xem common/utils/chunkReload.ts.
registerChunkReload();

// Khắc phục lỗi React crash khi dùng tính năng dịch của trình duyệt (Google Translate / browser translation).
installGoogleTranslateDOMPatch();

createRoot(document.getElementById('root')!).render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <ConnectedIntlProvider
        formats={{
          number: {
            percentWith2Decimals: {
              style: 'percent',
              maximumFractionDigits: 2,
            },
          },
        }}
      >
        <RouterProvider router={router} future={{ v7_startTransition: true }} />
      </ConnectedIntlProvider>
    </PersistGate>
  </Provider>
);

// App đã render: chạy êm một lúc thì mở lại quyền tự tải lại cho lần lỗi chunk sau (nếu vừa tải
// lại mà vẫn lỗi ngay thì cờ còn nguyên → không reload vòng vo).
markChunkReloadRecovered();
